import logger from '../logger';
import { isBrowser } from '../utils/EnvUtils';
import { isPromise } from '../utils/Utils';

type PerformanceContextType =
    | 'action'
    | 'asyncGetter'
    | 'chatbotEventHandler'
    | 'message'
    | 'scheduledAction'
    | 'sharedStateMessage';

export type PerformanceContext = { name: string; type: PerformanceContextType; userId: string };

type PerformanceLogEvent = {
    apiMethodName?: string;
    context: PerformanceContext;
    durationMs: number;
};

export function withPerformanceTracking<T extends Function>(
    operation: T,
    context: PerformanceContext,
    apiMethodName?: string,
    opts?: { asyncOnly?: boolean },
): T {
    if (isBrowser()) {
        return operation; // Only track performance on the server
    }

    const wrappedOperation = (...args: unknown[]): unknown => {
        const t0 = performance.now();

        const result = operation(...args);

        if (isPromise(result)) {
            return result.then((promiseResult) => {
                const durationMs = Math.round(performance.now() - t0);
                logPerformance({ context, durationMs, apiMethodName });

                return promiseResult;
            });
        } else if (opts?.asyncOnly !== true) {
            const durationMs = Math.round(performance.now() - t0);
            logPerformance({ context, durationMs, apiMethodName });
        }

        return result;
    };

    return wrappedOperation as unknown as T;
}

function logPerformance(e: PerformanceLogEvent): void {
    logger.log({
        logType: 'perf', // A CloudWatch subscription filter matches against this property
        context: e.context,
        durationMs: e.durationMs,
        apiMethodName: e.apiMethodName,
    });
}

/** Wrap each async method in `obj` with performance tracking, including nested methods. */
export function wrapWithPerformanceTracking<T extends object>(obj: T, ctx: PerformanceContext, prefix = 'api.'): void {
    if (isBrowser()) {
        return; // Only track performance on the server
    }

    for (const key in obj) {
        const val = obj[key];

        if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            wrapWithPerformanceTracking(val, ctx, `${prefix}${key}.`);
        } else if (typeof val === 'function') {
            obj[key] = withPerformanceTracking(val, ctx, prefix + key, { asyncOnly: true });
        }
    }
}

export function isSampled(): boolean {
    return Math.random() <= getSampleRate();
}

let cachedSampleRate: number | undefined;

function getSampleRate(): number {
    if (cachedSampleRate !== undefined) {
        return cachedSampleRate;
    }

    let configuredRate: string | undefined;
    try {
        configuredRate = process.env.PERFORMANCE_TRACKING_SAMPLE_RATE;
    } catch {
        // Ignore missing process.env in browser environment
    }

    if (configuredRate) {
        const sampleRate = Number(configuredRate);
        const isValidSampleRate = sampleRate >= 0 && sampleRate <= 1;

        if (!isValidSampleRate) {
            throw Error(
                `Invalid PERFORMANCE_TRACKING_SAMPLE_RATE env var value: ${configuredRate}. Enter a number between 0 and 1, inclusive.`,
            );
        }

        cachedSampleRate = sampleRate;
    } else {
        cachedSampleRate = 0.001; // Default sample rate is 0.1%
    }

    return cachedSampleRate;
}
